<!--
 * Path: packages/frontend/src/core/components/forms/cairoMatrix/CairoMatrixRead.vue
 * Task: Displays the CAIRO responsibility matrix in read-only mode using a native Vuetify v-table.
 * Update: Made layout fluid (100% width) to match other form components like Timeline, removing hardcoded excessive min-widths.
-->
<template>
  <div :class="{ 'mb-6': !hideHeader }">
    <SubHeader v-if="!hideHeader" :title="field.name || 'Responsibility Matrix'" :info="field.info" @click:info="$emit('show-info')" />

    <v-card variant="outlined" class="mt-2 bg-surface">
      <div class="matrix-scroll-container custom-scrollbar">
        <v-table hover class="cairo-table">
          <thead>
          <tr>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 120px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-key</v-icon>Acc.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 120px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-hard-hat</v-icon>Resp.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 120px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-star</v-icon>Cons.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 120px;">
              <v-icon size="small" class="me-1 text-primary d-none d-sm-inline">mdi-account-eye</v-icon>Inf.
            </th>
            <th class="text-left text-caption font-weight-bold text-uppercase" style="width: 20%; min-width: 120px;">
              <v-icon size="small" class="me-1 text-error d-none d-sm-inline">mdi-account-cancel</v-icon>Omit.
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td class="pa-2" style="vertical-align: top;">
              <div class="d-flex flex-column gap-1 w-100">
                <EntityPanel
                  v-for="item in resolveItems('accountableIds')"
                  :key="item.id"
                  :entity="item"
                  is-link
                  no-border
                  class="bg-background elevation-0 border mb-1"
                  @click="openDetails(item.id)"
                />
                <div v-if="resolveItems('accountableIds').length === 0" class="text-caption text-disabled text-center py-2">-</div>
              </div>
            </td>
            <td class="pa-2" style="vertical-align: top;">
              <div class="d-flex flex-column gap-1 w-100">
                <EntityPanel
                  v-for="item in resolveItems('responsibleIds')"
                  :key="item.id"
                  :entity="item"
                  is-link
                  no-border
                  class="bg-background elevation-0 border mb-1"
                  @click="openDetails(item.id)"
                />
                <div v-if="resolveItems('responsibleIds').length === 0" class="text-caption text-disabled text-center py-2">-</div>
              </div>
            </td>
            <td class="pa-2" style="vertical-align: top;">
              <div class="d-flex flex-column gap-1 w-100">
                <EntityPanel
                  v-for="item in resolveItems('consultedIds')"
                  :key="item.id"
                  :entity="item"
                  is-link
                  no-border
                  class="bg-background elevation-0 border mb-1"
                  @click="openDetails(item.id)"
                />
                <div v-if="resolveItems('consultedIds').length === 0" class="text-caption text-disabled text-center py-2">-</div>
              </div>
            </td>
            <td class="pa-2" style="vertical-align: top;">
              <div class="d-flex flex-column gap-1 w-100">
                <EntityPanel
                  v-for="item in resolveItems('informedIds')"
                  :key="item.id"
                  :entity="item"
                  is-link
                  no-border
                  class="bg-background elevation-0 border mb-1"
                  @click="openDetails(item.id)"
                />
                <div v-if="resolveItems('informedIds').length === 0" class="text-caption text-disabled text-center py-2">-</div>
              </div>
            </td>
            <td class="pa-2" style="vertical-align: top;">
              <div class="d-flex flex-column gap-1 w-100">
                <EntityPanel
                  v-for="item in resolveItems('omittedIds')"
                  :key="item.id"
                  :entity="item"
                  is-link
                  no-border
                  class="bg-background elevation-0 border mb-1"
                  @click="openDetails(item.id)"
                />
                <div v-if="resolveItems('omittedIds').length === 0" class="text-caption text-disabled text-center py-2">-</div>
              </div>
            </td>
          </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useDatastoreStore } from '@/store/datastore';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';

import SubHeader from '@/core/components/forms/SubHeader.vue';
import EntityPanel from '@/core/components/common/EntityPanel.vue';

interface FieldProps {
  id?: string;
  name?: string;
  info?: string;
  type?: string;
  entitySource?: string;
}

const props = withDefaults(defineProps<{
  field: FieldProps;
  hideHeader?: boolean;
}>(), {
  hideHeader: false
});

const emit = defineEmits<{ (e: 'show-info'): void; }>();

const datastoreStore = useDatastoreStore();
const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const formContext = inject(FormContextKey, null);

const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const inverseMap: Record<string, string> = {
  accountableIds: 'accountableForartifactIds',
  responsibleIds: 'responsibleForartifactIds',
  consultedIds: 'consultedForartifactIds',
  informedIds: 'informedForartifactIds',
  omittedIds: 'omittedFromartifactIds'
};

const resolveItems = (raciKey: string) => {
  if (!activeEntity.value || !activeEntity.value.raci) return [];
  const isInverse = props.field.type === 'inverse_relation';
  const actualKey = isInverse ? inverseMap[raciKey] : raciKey;
  const ids = activeEntity.value.raci[actualKey] || [];

  return ids.map((id: string) => {
    // Look up the exact entity from the global flattened list
    const found = datastoreStore.allEntities.find((e: Entity) => e.id === id);
    return found ? found : { id, title: id, entityType: 'unknown' };
  });
};

const openDetails = (id: string) => {
  workspaceStore.setSecondarySelectedEntity(id);
  uiStore.panels.showSecondaryDetails = true;
};
</script>

<style scoped>
.gap-1 { gap: 4px; }

/* Ensure the table attempts to fit within its container
   but maintains a basic minimum layout integrity */
.cairo-table {
  width: 100%;
}
.cairo-table :deep(table) {
  min-width: 600px; /* Reduced from 1000px to allow fitting in typical modals */
  table-layout: fixed;
}

/* Scroll container wrapper */
.matrix-scroll-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Custom scrollbar styling for a cleaner look */
.custom-scrollbar::-webkit-scrollbar {
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.15);
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-on-surface), 0.3);
}

/* Entity Panel overrides for tighter matrix packing */
:deep(.entity-panel .v-card-text) {
  padding: 4px 8px !important;
}
</style>
